// FE-SCRM-057 — ProposalUploadModal.
//
// Multipart upload to ``POST /opportunities/{id}/proposals``. File is
// required; notes is optional. The recipient picker per spec is a
// MARK-SENT-time concern (the BE upload endpoint doesn't take a
// recipient); we keep recipient flow on the dedicated mark-sent modal
// to mirror BE-SCRM-029's contract.
//
// Version is auto-incremented per opportunity by the BE inside the
// transaction — no client-side input.

import { useState } from "react";

import { Modal } from "@/design-system";


export interface ProposalUploadModalProps {
  opportunityId: string;
  prospectId: string;
  isPending: boolean;
  onSubmit: (input: { file: File; notes: string | null }) => void;
  onClose: () => void;
}


export function ProposalUploadModal({
  isPending, onSubmit, onClose,
}: ProposalUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!file) {
      setError("Pick a file to upload.");
      return;
    }
    setError(null);
    onSubmit({ file, notes: notes.trim() ? notes.trim() : null });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Upload proposal"
      size="md"
      footer={
        <>
          <button
            type="button"
            data-testid="proposal-upload-cancel"
            onClick={onClose}
            disabled={isPending}
            className={
              "rounded-md border border-border-subtle px-3 py-1.5 text-sm "
              + "text-text-primary hover:bg-recessed disabled:opacity-60"
            }
          >
            Cancel
          </button>
          <button
            type="submit"
            form="proposal-upload-form"
            data-testid="proposal-upload-submit"
            disabled={isPending}
            className={
              "rounded-md bg-teal px-3 py-1.5 text-sm font-medium "
              + "text-text-inverse hover:opacity-90 disabled:opacity-60"
            }
          >
            {isPending ? "Uploading…" : "Upload"}
          </button>
        </>
      }
    >
      <form
        id="proposal-upload-form"
        data-testid="proposal-upload-modal"
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
      >
        {error && (
          <div
            role="alert"
            data-testid="proposal-upload-error"
            className="rounded-md bg-ember/15 px-3 py-2 text-sm text-ember"
          >
            {error}
          </div>
        )}

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">
            File (PDF)
          </span>
          <input
            type="file"
            accept="application/pdf"
            data-testid="proposal-upload-file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm text-text-primary"
          />
          <span className="text-xs text-text-tertiary">
            Version is auto-assigned per opportunity (v1, v2, …).
          </span>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">Notes</span>
          <textarea
            data-testid="proposal-upload-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="e.g. Final stack-comparison deck."
            className={
              "rounded-md border border-border-subtle bg-raised px-2 "
              + "py-1.5 text-sm"
            }
          />
        </label>
      </form>
    </Modal>
  );
}
